import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SectionPliable from '../../../composants/SectionPliable/SectionPliable';
import Banner from '../../../composants/Banner/Banner';
import Button from '../../../composants/Button/Button';
import ValidationPopUp from '../../../composants/ValidationPopUp/ValidationPopUp';
import { PrintButton } from '../../../composants/PrintButton/PrintButton';
import NextTabButton from '../../../composants/NextTabButton/NextTabButton';
import OperationDescriptifAdministratif from '../../SuiviOperationMSV2/components/OperationDescriptifAdministratif';
import OperationProceduralRecap from '../../SuiviOperationMSV2/components/OperationProceduralRecap';
import { buildConsultationFormData } from '../../SuiviOperationMSV2/domain/operationConsultation';
import { OperationMS } from '../../SuiviOperationMSV2/types';
import DocumentsList from '../../../composants/DocumentsList/DocumentsList';
import { useDocuments } from '../../../contexts/DocumentsContext';
import { getStatusColor } from '../../../utils/entityUtils';
import {
  canRefuserConsultation,
  isConsultationEnCours,
  dateLimiteRemiseOffres,
  depotOffre,
} from '../domain/commandeMsCandidature';
import { ctaVersOffre } from '../domain/commandeMsNextTab';
import { useChronoExpired } from '../hooks/useChronoExpired';
import { refuserConsultation } from '../services/commandeMsService';
import { CommandeMS, TabKind } from '../types';
// Les classes opms__* (grille descriptif, doc technique, cahier des charges) vivent dans la CSS de la
// page acheteur ; les sous-composants viewMode reutilises ne l'importent pas eux-memes -> on la charge.
import '../../SuiviOperationMSV2/SuiviOperationMSV2.css';

interface CommandeMsConsultationProps {
  commande: CommandeMS;
  // Bascule d'onglet par `kind` (fournie par le shell) : "Déposer mon offre" / "Voir l'offre" -> Offre.
  goToTab?: (kind: TabKind) => void;
}

// Onglet "Consultation" de la vue PRESTATAIRE commande-MS : miroir LECTURE SEULE du descriptif que
// l'acheteur a defini (onglet Consultation acheteur). On reutilise les sous-composants viewMode
// acheteur (Descriptif Administratif + Procedural) alimentes par la meme vue-modele
// (buildConsultationFormData, qui lit les colonnes de l'operation). Le Descriptif Technique et le
// Financier (DPGF = les lignes de la commande) sont rendus en direct, en lecture seule.
//
// Actions (hors zone imprimee) : impression de l'onglet, refus de la consultation (meme gating et
// meme service que l'onglet Offre), et raccourci vers l'onglet Offre.
const CommandeMsConsultation: React.FC<CommandeMsConsultationProps> = ({ commande, goToTab }) => {
  const navigate = useNavigate();

  // La commande WRAPPE l'operation (colonnes du descriptif). Cast : OperationRef porte les memes
  // colonnes que celles lues par buildConsultationFormData (index signature `[key]: unknown`).
  const operation = (commande.operation ?? {}) as unknown as OperationMS;
  const formData = buildConsultationFormData(operation);
  const articles = commande.articles ?? [];

  // Zone imprimee : le descriptif seul (la barre d'actions est en dehors du ref).
  const printRef = useRef<HTMLDivElement>(null);

  // Expiration "vivante" : le chrono partage emet 'chronoExpired' a zero (comme l'onglet Offre).
  const chronoExpired = useChronoExpired();

  const canRefuser = canRefuserConsultation(commande, chronoExpired);
  // Bandeau de rappel du delai : uniquement pendant la consultation.
  const dateLimite = isConsultationEnCours(commande, chronoExpired) ? dateLimiteRemiseOffres(commande) : null;
  // Bandeau de confirmation : qui a depose l'offre, et quand.
  const depot = depotOffre(commande);
  // CTA de bascule vers l'onglet Offre. Seul le LIBELLE suit la fenetre de depot (inviter a deposer,
  // ou donner acces en lecture une fois l'offre deposee) : la couleur, elle, ne varie pas — c'est la
  // seule action de navigation de l'onglet, donc son CTA. Regle portee par le domaine.
  const ctaOffre = ctaVersOffre(commande, chronoExpired);

  const [refuserOpen, setRefuserOpen] = useState(false);
  const [refusSuccessOpen, setRefusSuccessOpen] = useState(false);
  const [errorModal, setErrorModal] = useState<string | null>(null);

  // Bascule vers l'onglet Offre. Le scroll en haut de page est porte par le CTA partage
  // (NextTabButton) ET par `goToTab` (shell) : ce handler ne fait que la bascule.
  const handleAllerAOffre = () => goToTab?.(ctaOffre.target);

  const handleRefuser = async () => {
    setRefuserOpen(false);
    try {
      await refuserConsultation(commande.id);
      setRefusSuccessOpen(true);
    } catch (err) {
      setErrorModal(err instanceof Error ? err.message : 'Erreur lors du refus de la consultation');
    }
  };

  // Pieces de l'accord-cadre en vigueur (CCAP/CCTP...), chargees app-wide par le DocumentsProvider.
  // Meme mapping que cote acheteur (OperationConsultation) : filtre actif -> format DocumentsList.
  const { commonDocuments, loading: acDocsLoading } = useDocuments();
  const piecesAccordCadre = commonDocuments
    .filter((d) => d.actif)
    .map((d) => ({
      id: d.id,
      type: d.type,
      updated_at: d.updated_at,
      displayLabel: d.nom,
      category: 'commun' as const,
    }));

  // Banniere du PDF imprime : memes valeurs que CommandeMsHeader.
  const lotTechnique = commande.operation?.lot_technique;
  const idLotDepartement = commande.operation?.id_lot_departement;
  const departement = commande.operation?.lot_departement?.departement;
  const subtitle = lotTechnique
    ? `${lotTechnique.id} - ${lotTechnique.nom}, ${idLotDepartement} - ${departement?.nom ?? ''}`
    : 'Informations de lot non disponibles';

  // Bandeaux d'etat, rendus EN HAUT et EN BAS de l'onglet (juste avant les actions) : le descriptif
  // est long, le prestataire ne doit pas avoir a remonter pour retrouver le delai ou la confirmation.
  // Hors de la zone d'impression (printRef) : ils ne partent pas dans le PDF.
  const bandeauxEtat = (
    <>
      {dateLimite && (
        <Banner variant="warning" message={`Vous êtes invités à déposer votre offre jusqu'au ${dateLimite}`} />
      )}
      {depot && (
        <Banner variant="success" message={`Offre déposée par ${depot.nom} le ${depot.dateHeure}`} />
      )}
    </>
  );

  return (
    <>
      {bandeauxEtat}

      <div className="opms__consultation" ref={printRef}>
        {/* Descriptif Administratif (miroir acheteur, lecture seule) */}
        <SectionPliable title="Descriptif Administratif" defaultOpen>
          <div className="section-content">
            <OperationDescriptifAdministratif
              operation={operation}
              formData={formData}
              viewMode
              errors={{}}
            />
          </div>
        </SectionPliable>

        {/* Descriptif Technique : attestation BPU + documentation + cahier des charges (lecture seule) */}
        <SectionPliable title="Descriptif Technique" defaultOpen>
          <Banner
            variant="info"
            message="Les informations écrites ci-dessous sont contractuelles y compris les pièces éventuellement chargées au travers du lien URL. Mais, toutes précisions administratives ou juridiques de nature à contrevenir à l'accord-cadre général sans dérogations prévues au contrat sont à considérer comme inexistantes dans ce descriptif"
          />
          <div className="section-content">
            <div className="form-group">
              {formData.attestationBPU && (
                <div className="opms__attestation-display">
                  Attestation : les BPU des bons de commande ne correspondent pas au besoin, une consultation
                  sur-mesure est nécessaire.
                </div>
              )}
              <div className="opms__descriptif-admin opms__doc-technique">
                <div className="opms__admin-grid">
                  <div className="form-group" style={{ gridColumn: 'span 4' }}>
                    <label>URL vers la doc technique et/ou planning détaillé</label>
                    <div className="input-inactif">{formData.urlDocTechnique || '-'}</div>
                  </div>
                  <div className="form-group" style={{ gridColumn: 'span 4' }}>
                    <label>Modalité d'accès à la documentation</label>
                    <div className="input-inactif">{formData.modaliteAccesDocumentation || '-'}</div>
                  </div>
                  <div className="form-group" style={{ gridColumn: 'span 4' }}>
                    <label>Liste des pièces éventuelles composant la documentation technique</label>
                    <div className="input-inactif">{formData.listePiecesDocumentation || '-'}</div>
                  </div>
                </div>
              </div>
              <label>Cahier des charges</label>
              <div className="input-inactif opms__besoin-readonly">{formData.besoin || '-'}</div>
            </div>
          </div>
        </SectionPliable>

        {/* Descriptif Financier : le DPGF (lignes de la commande), lecture seule, sans prix. */}
        <SectionPliable title="Descriptif Financier" defaultOpen>
          <div className="section-content">
            {articles.length > 0 ? (
              <table className="articles-table">
                <thead>
                  <tr>
                    <th className="financier-numero-article">N°</th>
                    <th>Description de la prestation</th>
                  </tr>
                </thead>
                <tbody>
                  {articles.map((ligne, index) => (
                    <tr key={ligne.id ?? index}>
                      <td className="financier-numero-article">{ligne.code}</td>
                      <td>{ligne.nom}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="input-inactif">Aucun article défini pour cette consultation.</div>
            )}
          </div>
        </SectionPliable>

        {/* Descriptif procédural (miroir acheteur, lecture seule) */}
        <SectionPliable title="Descriptif procédural" defaultOpen>
          <div className="section-content">
            <OperationProceduralRecap
              operation={operation}
              formData={formData}
              viewMode
              errors={{}}
            />

            {/* Documents liés à la consultation : pieces de l'accord-cadre (CCTP...) cliquables + a droite
                le champ des pieces specifiques (miroir read-only du Descriptif technique). */}
            <div className="opms__descriptif-admin" style={{ marginTop: '2rem' }}>
              <div className="form-group">
                <label>
                  Documents liés à la consultation <span style={{ color: 'red' }}>*</span>
                </label>
                {acDocsLoading && piecesAccordCadre.length === 0 ? (
                  <div className="input-inactif">Chargement des pièces…</div>
                ) : (
                  <DocumentsList
                    documents={piecesAccordCadre}
                    variant="grid"
                    trailingCard={
                      <div className="opms__docs-consultation-champ">
                        <span className="opms__docs-consultation-champ-label">
                          Liste des pièces éventuelles composant la documentation technique
                        </span>
                        <div className="opms__docs-consultation-champ-inner">
                          {formData.listePiecesDocumentation || '-'}
                        </div>
                      </div>
                    }
                  />
                )}
              </div>
            </div>
          </div>
        </SectionPliable>
      </div>

      {/* Rappel des bandeaux d'etat en pied d'onglet, juste avant les actions. */}
      {bandeauxEtat}

      {/* Actions : hors du ref d'impression (no-print) */}
      <div className="action-buttons no-print">
        {canRefuser && (
          <Button
            value="Refuser la consultation"
            variant="cancel"
            onClick={() => setRefuserOpen(true)}
          />
        )}

        <PrintButton
          targetRef={printRef}
          fileName={`${commande.operation?.numero ?? ''} - Consultation MS`}
          value="Imprimer la consultation"
          suiviHeaderProps={{
            titre: commande.operation?.nom ?? 'Marché subséquent',
            sousTitre: '',
            subtitle,
            status: commande.status?.nom || 'Statut inconnu',
            statusColor: getStatusColor(commande.status?.nom || ''),
            delais: commande.expire_at ?? commande.operation?.expire_at ?? undefined,
          }}
        />

        <NextTabButton label={ctaOffre.label} hidden={ctaOffre.hidden} onClick={handleAllerAOffre} />
      </div>

      {/* Confirmation de refus */}
      <ValidationPopUp
        isOpen={refuserOpen}
        title="Refuser la consultation"
        text="Vous vous apprêtez à refuser de candidater à ce marché subséquent. Cette action est irréversible."
        onClose={() => setRefuserOpen(false)}
        onConfirm={handleRefuser}
        cancelLabel="Annuler"
        confirmLabel="Refuser"
      />

      {/* Succès du refus */}
      <ValidationPopUp
        isOpen={refusSuccessOpen}
        title="Consultation refusée"
        text="La consultation a été refusée avec succès. Vous allez être redirigé vers la liste des commandes."
        onClose={() => navigate('/commandes')}
        onConfirm={() => navigate('/commandes')}
        cancelLabel=""
        confirmLabel="OK"
      />

      {/* Erreur d'action */}
      <ValidationPopUp
        isOpen={errorModal !== null}
        title="Erreur"
        text={errorModal ?? ''}
        onClose={() => setErrorModal(null)}
        onConfirm={() => setErrorModal(null)}
        cancelLabel=""
        confirmLabel="OK"
      />
    </>
  );
};

export default CommandeMsConsultation;
